/**
 * MD5 计算器主线程类
 * 专注于核心功能：稳定、简洁的 MD5 计算接口
 *
 * 特点：
 * - 简洁的 API，易于使用
 * - 专注于稳定性和可读性
 * - 最小化的错误处理
 */
import Md5Worker from './md5.worker.js';
import { getCurrentSystem } from '@umu/umu-util';
class MD5Calculator {
    static worker = null;
    static requestId = 0;
    static activeRequests = new Map();

    /**
     * 获取系统对应的 SparkMD5 CDN URL
     */
    static getSparkMD5Url() {
        try {
            const system = getCurrentSystem();

            const cdnMap = {
                CN: 'https://statics-cdn-cn.umucdn.cn/statcis/libs/md5/spark-md5.min.js',
                TW: 'https://d1yemi1ss2f2oh.cloudfront.net/statcis/libs/md5/spark-md5.min.js',
                JP: 'https://dpfrzbu0cqnmm.cloudfront.net/statcis/libs/md5/spark-md5.min.js',
                EN: 'https://d1bvk99i2a79wx.cloudfront.net/statcis/libs/md5/spark-md5.min.js',
                EM: 'https://dmks9efzatfsk.cloudfront.net/statcis/libs/md5/spark-md5.min.js',
            };

            return cdnMap[system] || cdnMap.CN;
        } catch (error) {
            console.warn('[MD5 Calculator] Failed to get system CDN, using default:', error.message);
            return 'https://statics-cdn-cn.umucdn.cn/statcis/libs/md5/spark-md5.min.js';
        }
    }

    /**
     * 初始化 Worker
     */
    static initWorker() {
        if (!this.worker) {
            try {
                this.worker = new Md5Worker();

                this.worker.onmessage = (e) => {
                    this.handleWorkerMessage(e.data);
                };

                this.worker.onerror = (error) => {
                    console.error('[MD5 Calculator] Worker error:', error);
                    this.rejectAllRequests(new Error(`Worker error: ${error.message}`));
                };

                console.log('[MD5 Calculator] Worker initialized');
            } catch (error) {
                throw new Error(`Failed to create MD5 Worker: ${error.message}`);
            }
        }
        return this.worker;
    }

    /**
     * 处理 Worker 消息
     */
    static handleWorkerMessage(data) {
        const { type, data: messageData, requestId } = data;
        const request = this.activeRequests.get(requestId);

        if (!request) {
            return;
        }

        switch (type) {
            case 'complete':
                this.activeRequests.delete(requestId);
                request.resolve(messageData.hash);
                break;

            case 'error':
                this.activeRequests.delete(requestId);
                request.reject(new Error(messageData.message));
                break;

            case 'progress':
                if (request.onProgress) {
                    request.onProgress(messageData);
                }
                break;
        }
    }

    /**
     * 拒绝所有请求
     */
    static rejectAllRequests(error) {
        for (const [_requestId, request] of this.activeRequests) {
            request.reject(error);
        }
        this.activeRequests.clear();
    }

    /**
     * 生成请求 ID
     */
    static generateRequestId() {
        return ++this.requestId;
    }

    /**
     * 计算文件的 MD5 哈希值
     * @param {File} file - 要计算的文件
     * @param {Function} onProgress - 进度回调（可选）
     * @returns {Promise<string>} MD5 哈希值
     */
    static async calculateFileHash(file, onProgress) {
        if (!file || !(file instanceof File)) {
            throw new Error('Invalid file parameter');
        }
        if (!this.isSupported()) {
            throw new Error('MD5 calculation not supported in this browser');
        }

        const worker = this.initWorker();
        const requestId = this.generateRequestId();

        return new Promise((resolve, reject) => {
            // 存储请求信息
            const request = {
                resolve,
                reject,
                onProgress,
                startTime: Date.now(),
            };

            this.activeRequests.set(requestId, request);

            // 发送计算请求
            worker.postMessage({
                type: 'calculate',
                file: file,
                requestId,
                sparkMD5Url: this.getSparkMD5Url(),
            });

            console.log(`[MD5 Calculator] Started calculation for ${file.name} (${this.formatSize(file.size)})`);
        });
    }

    /**
     * 计算文件 MD5 并支持进度回调（简化接口）
     */
    static async calculateWithProgress(file, onProgress) {
        return this.calculateFileHash(file, onProgress);
    }

    /**
     * 获取活跃任务数量
     */
    static getActiveTaskCount() {
        return this.activeRequests.size;
    }

    /**
     * 格式化文件大小
     */
    static formatSize(bytes) {
        if (bytes === 0) {
            return '0 Bytes';
        }

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * 检查浏览器兼容性
     */
    static isSupported() {
        return typeof Worker !== 'undefined' && typeof File !== 'undefined' && typeof Promise !== 'undefined';
    }

    /**
     * 清理资源
     */
    static dispose() {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }

        this.activeRequests.clear();
        this.requestId = 0;

        console.log('[MD5 Calculator] Resources cleaned up');
    }
}

export default MD5Calculator;
